import { Page, PageHeader } from "@commerce/components/page";
import { LogsScreen } from "@commerce/logs/LogsScreen";
import { DEFAULT_WINDOW_HOURS, LogsSkeleton } from "@commerce/logs/LogsView";
import {
	logErrorsQuery,
	logsQuery,
	logsSearchSchema,
} from "@commerce/logs/meta";
import { isOrganizationAdmin } from "@repo/auth/lib/helper";
import { config } from "@repo/config";
import { AdminOnlyNotice } from "@saas/settings/components/SettingsLayout";
import { AsyncBoundary } from "@shared/components/AsyncBoundary";
import { dehydrateForLoader, getServerQueryClient } from "@shared/lib/server";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/_saas/app/_org/$organizationSlug/logs")({
	validateSearch: logsSearchSchema,
	beforeLoad: ({ context }) => ({
		isOrganizationAdmin: isOrganizationAdmin(
			context.organization,
			context.session.user,
		),
	}),
	// Prefetch the view the URL asks for once, on entry. Filter changes after that are
	// client queries, not loader runs.
	shouldReload: false,
	loader: async ({ context, location }) => {
		const organizationId = context.organization.id;
		const queryClient = getServerQueryClient();
		if (context.isOrganizationAdmin) {
			const search = logsSearchSchema.parse(location.search);
			await Promise.all([
				queryClient.ensureInfiniteQueryData(
					logsQuery(organizationId, search),
				),
				queryClient.ensureQueryData(
					logErrorsQuery(
						organizationId,
						search.hours ?? DEFAULT_WINDOW_HOURS,
					),
				),
			]);
		}
		return {
			organizationId,
			dehydratedState: dehydrateForLoader(queryClient),
		};
	},
	head: () => ({ meta: [{ title: `Logs - ${config.appName}` }] }),
	component: LogsPage,
});

function LogsPage() {
	const { isOrganizationAdmin } = Route.useRouteContext();
	const { organizationId, dehydratedState } = Route.useLoaderData();
	if (!isOrganizationAdmin) {
		return (
			<Page width="narrow">
				<AdminOnlyNotice what="the logs" />
			</Page>
		);
	}
	return (
		<AsyncBoundary
			fallback={
				<Page width="wide">
					<PageHeader title="Logs" />
					<LogsSkeleton />
				</Page>
			}
			dehydratedState={dehydratedState}
			errorFallback="inline"
		>
			<LogsScreen organizationId={organizationId} />
		</AsyncBoundary>
	);
}
